import React, { Suspense, lazy, useCallback, useMemo, useState } from 'react';
import { buildCompareItems } from '../components/compare/compare-items';

// Loaded on first use: keeps react-resizable-panels out of the initial map bundle.
const CompareView = lazy(() => import('../components/compare/CompareView'));

/**
 * State for the split/swipe comparison view (Wave 5.4, docs/BUILDER_GUIDE.md).
 *
 *   const compare = useCompareView({ mapRefHolder, data: all, vt });
 *   ...  {compare.element}  inside the map container;  compare.toggle / compare.active for the ribbon.
 *
 * `data` is MapView's state bag (layers, usgsData, importedLayers, resultLayers, rasterLayers,
 * serviceLayers, layerStyles, stylePaint, ...). Nothing is copied: side B renders the same
 * objects, so there are no extra fetches.
 */
export function useCompareView({ mapRefHolder, data, vt }) {
  const [active, setActive] = useState(false);
  const [mode, setMode] = useState('split');            // 'split' | 'swipe'
  const [orientation, setOrientation] = useState('vertical'); // vertical divider = left | right
  const [overridesA, setOverridesA] = useState({});     // { [itemKey]: false } hides on the main map
  const [overridesB, setOverridesB] = useState({});     // { [itemKey]: boolean } on the compare map
  const [rightBase, setRightBase] = useState(() => (data?.viewMode === 'satellite' ? 'map' : 'satellite'));

  const items = useMemo(() => buildCompareItems({
    layers: data?.layers, usgsData: data?.usgsData, importedLayers: data?.importedLayers,
    resultLayers: data?.resultLayers, rasterLayers: data?.rasterLayers, vtLayers: vt?.layers,
    serviceLayers: data?.serviceLayers,
  }), [data?.layers, data?.usgsData, data?.importedLayers, data?.resultLayers, data?.rasterLayers, vt?.layers, data?.serviceLayers]);

  const setOverride = useCallback((side, key, value) => {
    (side === 'a' ? setOverridesA : setOverridesB)((o) => ({ ...o, [key]: value }));
  }, []);
  const toggle = useCallback(() => setActive((v) => !v), []);
  const close = useCallback(() => { setActive(false); setOverridesA({}); }, []);

  const element = active ? (
    <Suspense fallback={null}>
    <CompareView
      mainMapRef={mapRefHolder} data={data} vt={vt} items={items}
      viewMode={data?.viewMode} setViewMode={data?.setViewMode}
      mode={mode} setMode={setMode} orientation={orientation} setOrientation={setOrientation}
      overridesA={overridesA} overridesB={overridesB} setOverride={setOverride}
      rightBase={rightBase} setRightBase={setRightBase} onClose={close}
    />
    </Suspense>
  ) : null;

  return { active, toggle, element, mode, items };
}
